import React, { useState } from 'react';
import pluralize from 'pluralize';
import { formatWithCommas } from 'helpers/formatters/number_formats';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuildingCircleArrowRight, faRectangleHistoryCirclePlus, faTableLayout } from '@fortawesome/pro-solid-svg-icons';
import AddToList from '../../properties/add_to_list';
import { connect, useSelector } from 'react-redux';
import OffMarketMapTableViewToggle from './off_market_map_table_view_toggle';
import { useMediaQuery } from 'react-responsive';
import { Button, FormCheck, OverlayTrigger, Tooltip } from 'react-bootstrap-5';
import { setSearchTableColumnKeys, setUpdateResultsOnMapMove } from 'bundles/connect/components/store/actions/ui';
import { updateSearchTableColumns } from '../../../helpers/prospect_api';
import ManageColumnsModal from './manage_columns_modal';
import EmbeddedMode, { BUILDOUT_CRM_MODE } from '../../embedded_mode';
import AddToRethinkList from '../../off_market_properties/add_to_rethink/add_to_rethink_list';
import ProductFeature from './product_feature';
import type { RootState } from '../../store/root_reducer';
import { selectEmbeddedMode } from '../../store/selectors/ui';
import AddPropertyToCrm from '../../off_market_properties/crm/add_property_to_crm';

type OffMarketResultsHeaderProps = {
  allResults: RootState['results']['offMarketResults'];
  criteria: RootState['criteria'];
  products: RootState['ui']['products'];
  searchTableFields?: Array<{ label: string; value: string }>;
  selectAll: boolean;
  selectedPropertyIds: Set<string>;
  setSearchTableColumnKeys: (columnKeys: Array<string>) => void;
  setUpdateResultsOnMapMove: (update: boolean) => void;
  showTableView: boolean;
  total: number;
  updateResultsOnMapMove: boolean;
  onToggleAllSelected: () => void;
}


function OffMarketResultsHeader(
  {
    allResults,
    criteria,
    selectAll,
    selectedPropertyIds,
    onToggleAllSelected,
    products,
    searchTableFields,
    setSearchTableColumnKeys,
    setUpdateResultsOnMapMove,
    showTableView,
    total,
    updateResultsOnMapMove
  }: OffMarketResultsHeaderProps
) {
  const showFullSizeButton = useMediaQuery({ minWidth: 1193 });
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const embeddedMode = useSelector(selectEmbeddedMode);

  const handleModalCancel = () => {
    setShowModal(false);
  };

  const handleModalSave = (newSelectedFields) => {
    setSaving(true);
    const searchTableKeys = newSelectedFields.map(field => field.value);
    updateSearchTableColumns({ connect_search_table_view_columns: searchTableKeys })
      .then(() => {
        setShowModal(false);
        setSearchTableColumnKeys(searchTableKeys);
      })
      .finally(() => setSaving(false));
  };

  // If someone is in table view and then cancels their account or sales logs into a free account we still want to
  // allow them to get out of table view
  if (showTableView && !products.prospecting) {
    return (
      <div
        className="p-3 d-flex align-items-center justify-content-end top-0 position-relative z-3 bg-white shadow-md flex-grow-0"
      >
        <OffMarketMapTableViewToggle />
      </div>
    );
  }

  return (
    <ProductFeature product="prospecting">
      <div
        className={`p-3 d-flex align-items-center justify-content-${showTableView ? 'end' : 'between'} top-0 position-relative z-3 bg-white shadow-md flex-grow-0`}
      >
        {showTableView && (
          <>
            <div className="fw-bold me-auto">{formatWithCommas(total)} {pluralize('Property', total)} Found</div>
            <div className="d-flex align-items-center gap-2">
              <div>Update results when map moves</div>
              <FormCheck
                checked={updateResultsOnMapMove}
                onChange={() => setUpdateResultsOnMapMove(!updateResultsOnMapMove)}
              />
            </div>
            {showFullSizeButton && (
              <div className="d-flex gap-4 align-items-center me-4">
                <Button variant="text-primary" onClick={() => setShowModal(!showModal)}>
                  <FontAwesomeIcon icon={faTableLayout} />
                  Manage Columns
                </Button>
                {(selectedPropertyIds.size > 0 || selectAll) && embeddedMode !== BUILDOUT_CRM_MODE && (
                  <AddToList
                    criteria={criteria}
                    itemCount={selectAll ? total : selectedPropertyIds.size}
                    itemIds={selectedPropertyIds}
                    selectAll={selectAll}
                  >
                    <Button variant="text-primary">
                      <FontAwesomeIcon icon={faRectangleHistoryCirclePlus} />
                      Add to List
                    </Button>
                  </AddToList>
                )}
                {selectedPropertyIds.size === 0 && !selectAll && embeddedMode !== BUILDOUT_CRM_MODE && (
                  <Button disabled variant="text-primary">
                    <FontAwesomeIcon icon={faRectangleHistoryCirclePlus} />
                    Add to List
                  </Button>
                )}
              </div>
            )}
            {!showFullSizeButton && (
              <div className="d-flex gap-4 align-items-center">
                <OverlayTrigger overlay={<Tooltip>Manage Columns</Tooltip>} placement="top">
                  <FontAwesomeIcon
                    className="p-2 text-primary clickable"
                    icon={faTableLayout}
                    onClick={() => setShowModal(!showModal)}
                  />
                </OverlayTrigger>
                {(selectedPropertyIds.size > 0 || selectAll) && embeddedMode !== BUILDOUT_CRM_MODE && (
                  <AddToList
                    criteria={criteria}
                    itemCount={selectAll ? total : selectedPropertyIds.size}
                    itemIds={selectedPropertyIds}
                    selectAll={selectAll}
                  >
                    <FontAwesomeIcon className="p-2 text-primary clickable" icon={faRectangleHistoryCirclePlus} />
                  </AddToList>
                )}
                {selectedPropertyIds.size === 0 && !selectAll && embeddedMode !== BUILDOUT_CRM_MODE && (
                  <FontAwesomeIcon className="p-2 text-muted" icon={faRectangleHistoryCirclePlus} />
                )}
              </div>
            )}
            <ManageColumnsModal
              initialSelectedColumns={searchTableFields || []}
              saving={saving}
              show={showModal}
              onHide={handleModalCancel}
              onSave={handleModalSave}
            />
          </>
        )}
        {!showTableView && (
          <>
            <label className="me-auto">
              <div className="d-flex gap-2">
                <input
                  checked={selectAll}
                  data-pendo-tag="results_select_all_checkbox"
                  disabled={allResults?.length === 0}
                  type="checkbox"
                  onChange={onToggleAllSelected}
                />
                <div>Select All</div>
              </div>
            </label>
            {(selectedPropertyIds.size > 0 || selectAll) && (
              <>
                <EmbeddedMode>
                  <AddToList
                    criteria={criteria}
                    itemCount={selectAll ? total : selectedPropertyIds.size}
                    itemIds={selectedPropertyIds}
                    selectAll={selectAll}
                  >
                    <Button variant="text-primary">
                      <FontAwesomeIcon className="text-primary clickable" icon={faRectangleHistoryCirclePlus} />
                      Add to List
                    </Button>
                  </AddToList>
                </EmbeddedMode>
                <EmbeddedMode display forRethink>
                  <AddToRethinkList
                    bulk
                    criteria={criteria}
                    itemCount={selectAll ? total : selectedPropertyIds.size}
                    itemIds={selectedPropertyIds}
                    selectAll={selectAll}
                  />
                </EmbeddedMode>
                <EmbeddedMode display forBuildoutCrm>
                  <AddPropertyToCrm
                    bulk
                    itemCount={selectAll ? total : selectedPropertyIds.size}
                    itemIds={selectedPropertyIds}
                    selectAll={selectAll}
                  />
                </EmbeddedMode>
              </>
            )}
            {selectedPropertyIds.size === 0 && !selectAll && (
              <>
                {embeddedMode !== BUILDOUT_CRM_MODE && (
                  <Button className="border-0 ms-auto" disabled variant="text-primary">
                    <FontAwesomeIcon className="text-muted clickable" icon={faRectangleHistoryCirclePlus} />
                    Add to List
                  </Button>
                )}
                <EmbeddedMode display forBuildoutCrm>
                  <Button className="border-0 ms-auto" disabled variant="text-primary">
                    <FontAwesomeIcon className="text-muted clickable" icon={faBuildingCircleArrowRight} />
                    Add Properties
                  </Button>
                </EmbeddedMode>
              </>
            )}
          </>
        )}
        <OffMarketMapTableViewToggle />
      </div>
    </ProductFeature>
  );
}

const mapStateToProps = ({
  criteria,
  results: {
    offMarketResults,
    totalOffMarketResults
  },
  ui: { products, showTableView, updateResultsOnMapMove }
}) => {
  return {
    allResults: offMarketResults,
    criteria,
    products,
    setSearchTableColumnKeys,
    setUpdateResultsOnMapMove,
    showTableView,
    total: totalOffMarketResults,
    updateResultsOnMapMove
  };
};
export default connect(
  mapStateToProps,
  { setSearchTableColumnKeys, setUpdateResultsOnMapMove }
)(OffMarketResultsHeader);
